/**
 * usePendingGifts — pending anonymous gift invites for the authenticated user.
 *
 * Endpoint: GET /api/v1/me/pending-gifts → { gifts: [{ id, openUrl, expiresAt }] }
 * JWT-gated (disabled without a JWT). Used by the GiftBanner so a recipient who
 * onboarded inside the Mini App can still find and open their gift.
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export interface PendingGift {
  id: string;
  openUrl: string;
  expiresAt: string;
}

interface PendingGiftsResponse {
  gifts: PendingGift[];
}

export function usePendingGifts(): { gifts: PendingGift[]; isLoading: boolean } {
  const jwt = useAuth((s) => s.jwt);

  const query = useQuery<PendingGiftsResponse, Error>({
    queryKey: ['me-pending-gifts'],
    queryFn: async () => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch('/api/v1/me/pending-gifts', jwt);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`pending_gifts_fetch_failed: ${res.status} ${body}`);
      }
      return (await res.json()) as PendingGiftsResponse;
    },
    enabled: !!jwt,
    staleTime: 30_000,
  });

  return { gifts: query.data?.gifts ?? [], isLoading: query.isLoading };
}
